import {
  msPlanBulkEditDataInterface,
  msPlanBulkEditDataInterfaceDataInstanceInterface,
  msPlanBulkEditFilterInterface,
  notesInterface,
} from 'Interfaces/FDPInterfaces/tmsInterfaces';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { tmsScreenServices } from 'services/tmsScreenServices';
import { convertIsoToDateString } from 'utils/dateUtilities';
import { deepCopy } from 'utils/deepCopy';

type MarketShareType = 'model' | 'old' | 'saved';

export const useMSPlanBulkEdit = () => {
  const [editData, setEditData] = useState<msPlanBulkEditDataInterface[]>([]);
  const [editDataCopy, setEditDataCopy] = useState<msPlanBulkEditDataInterface[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(0); // Start from 0
  const [filterData, setFilterData] = useState<msPlanBulkEditFilterInterface>({
    Ramping: '',
    Concept: '',
    MoleculeAndDosageForm: '',
    StrategyCode: '',
    SellingCategory: '',
    GFC: '',
    ConceptSort: '', // Add ConceptSort to filter data
    // COT: "",
  });
  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
  const [GFCNotesSet, setGFCNotesSet] = useState<Set<string>>(new Set());
  const [GFCEditedSet, setGFCEditedSet] = useState<Set<string>>(new Set());
  const [gfcCotSellingCategoryError, setgfcCotSellingCategoryError] = useState<Set<string>>(
    new Set()
  );
  const [gfcCotSellingCategorySaveLoading, setGfcCotSellingCategorySaveLoading] = useState<
    Set<string>
  >(new Set());
  const [isSaveAllLoading, setIsSaveloading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [nextPageLoading, setNextPageLoading] = useState<boolean>(false);

  // New states for accordion functionality
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [marketShareTypeSelection, setMarketShareTypeSelection] = useState<
    Map<string, MarketShareType>
  >(new Map()); // Changed to use GFC-COT key

  //   states for notes
  const [isNotesLoading, setIsNotesLoading] = useState<boolean>(false);
  const [notesData, setNotesData] = useState<notesInterface[]>();
  const [isNoteSaving, setIsNoteSaving] = useState<boolean>(false);
  const [isNoteEdited, setIsNoteEdited] = useState<Map<number, boolean>>(new Map());
  const [isNoteDeleted, setIsNoteDeleted] = useState<Map<number, boolean>>(new Map());

  const requestIdRef = useRef(0);

  const [_savingGFCCotSC, setSavingGFCCotSC] = useState<Set<string>>(new Set());
  const [singleSaveConfirmationModalVisibility, setSingleSaveConfirmationModalVisibility] =
    useState<boolean>(false);
  const [saveAllConfirmationModalVisibility, setSaveAllConfirmationModalVisibility] =
    useState<boolean>(false);
  const [isRouteChangeConfirmationPopUpVisible, setIsRouteChangeConfirmationPopUpVisible] =
    useState<boolean>(false);
  const [isCancelConfirmationPopVisible, setisCancelConfirmationPopVisible] =
    useState<boolean>(false);
  const [focussedRowForNoteAdd, setFocussedRowForNoteAdd] = useState<null | number>(null);
  const [focussedRowForSaving, setFocussedRowForSaving] = useState<null | number>(null);
  const [deletingNote, setDeletingNote] = useState<notesInterface | null>(null);
  const [isNoteDeleting, setIsNoteDeleting] = useState<boolean>(false);
  const [isDeleteClicked, setIsDeleteClicked] = useState<boolean>(false);
  const [_latestNote, setLatestNote] = useState<notesInterface>();
  const navigate = useNavigate();

  const getNotesData = async (gfc: number, SellingCategory: string) => {
    try {
      setIsNotesLoading(true);
      const data = await tmsScreenServices.getGFCNotes(gfc, SellingCategory);
      if (data) {
        setNotesData(data);
      }
    } catch (_error) {
      toast.error('Error while getting notes data');
    } finally {
      setIsNotesLoading(false);
    }
  };

  const handleAddNote = async (value: string) => {
    if (
      focussedRowForNoteAdd === null ||
      focussedRowForNoteAdd < 0 ||
      focussedRowForNoteAdd >= editData.length ||
      !editData[focussedRowForNoteAdd]
    )
      return false;
    const gfc = editData[focussedRowForNoteAdd].GFC;
    const cot = editData[focussedRowForNoteAdd].COT;
    const SellingCategory = editData[focussedRowForNoteAdd].SellingCategory;

    let data: notesInterface[] = [];
    try {
      setIsNoteSaving(true);
      if (value?.trim() === '') {
        toast.warning('Cannot save an empty note!!');
      } else {
        const payload = {
          gfc: gfc,
          note: value,
          planning_type: 'salesplan',
        };

        data = await tmsScreenServices.saveGFCNote(payload);

        const recentNote = data.sort((a, b) => b.ID - a.ID)[0];
        setLatestNote(recentNote);

        GFCNotesSet.add(`${gfc}-${cot}-${SellingCategory}-${recentNote.CreatedDate}`);

        const tempEditedError = new Set(gfcCotSellingCategoryError);
        tempEditedError.delete(`${gfc}-${cot}-${SellingCategory}`);
        setgfcCotSellingCategoryError(tempEditedError);
      }
      return true;
    } catch (_error) {
      toast.error('Something went wrong while saving note.!!');
    } finally {
      setNotesData(data);
      setIsNoteSaving(false);
    }
    return false;
  };

  const handleEditNote = async (id: number, note: string) => {
    try {
      setIsNoteEdited(prev => {
        const temp = new Map(prev);
        if (temp.has(id)) {
          temp.set(id, true);
        } else {
          temp.set(id, true);
        }
        return temp;
      });
      const data = await tmsScreenServices.updateNote(id, note);
      if (data.status && notesData) {
        notesData.map(item => {
          if (item.ID === id) {
            item.CreatedDate = convertIsoToDateString(data.lastUpdatedDate) || '';
          }
        });
        toast.success('Note Updated successfully.');
      }
    } catch (_error) {
      console.log('handleEditNote=> ', _error);
      toast.error('Something went wrong.');
    } finally {
      setIsNoteEdited(prev => {
        const temp = new Map(prev);
        if (temp.has(id)) {
          temp.set(id, false);
        } else {
          if (temp.get(id)) {
            temp.set(id, false);
          }
        }
        return temp;
      });
    }
  };

  const handleDeleteNote = async (id: number) => {
    if (focussedRowForNoteAdd === null) return;
    const deletedNote = notesData?.filter(item => item.ID === id)[0];
    const editedRow = editData[focussedRowForNoteAdd];

    const gfc = editedRow.GFC;
    const cot = editedRow.COT;
    const sellCat = editedRow.SellingCategory;

    try {
      setIsNoteDeleting(true);
      const data = await tmsScreenServices.deleteNote(id);
      if (data) {
        toast.success('Note deleted successfully.');
        if (notesData) {
          // removing the deleted note from the state in the application after successful deletion.
          const notes = notesData.filter(note => {
            if (note.ID !== id) {
              return note;
            }
          });

          setNotesData([...notes]);
        }
      }
    } catch (_error) {
      toast.error('Something went wrong.');
    } finally {
      if (GFCNotesSet.has(`${gfc}-${cot}-${sellCat}-${deletedNote?.CreatedDate}`)) {
        GFCNotesSet.delete(`${gfc}-${cot}-${sellCat}-${deletedNote?.CreatedDate}`);
      }
      setIsNoteDeleting(false);
      setIsDeleteClicked(false);
    }
  };

  const getGFCCOTKey = (rowIndex: number): string => {
    const data = editData[rowIndex];
    return `${data.GFC}-${data.COT}-${data.SellingCategory}`;
  };

  const getAvailableMarketShareTypes = (rowIndex: number): MarketShareType[] => {
    const rowData = editData[rowIndex];
    const availableTypes: MarketShareType[] = ['model']; // Model is always available

    // Check if any month has saved values
    const hasSavedValues = rowData.data.some(item => item.isSavedMarketSharePresent);

    // Check if any month has old values
    const hasOldValues = rowData.data.some(item => item.isOldMarketSharePresent);

    if (hasOldValues) {
      availableTypes.push('old');
    }

    if (hasSavedValues) {
      availableTypes.push('saved');
    }

    return availableTypes;
  };

  const getDefaultMarketShareType = (rowIndex: number): MarketShareType => {
    const rowData = editData[rowIndex];

    // Check if any month has saved values
    const hasSavedValues = rowData.data.some(item => item.isSavedMarketSharePresent);
    if (hasSavedValues) {
      return 'saved'; // Saved has highest priority
    }

    // Check if any month has old values
    const hasOldValues = rowData.data.some(item => item.isOldMarketSharePresent);
    if (hasOldValues) {
      return 'old'; // Old has second priority
    }

    return 'model'; // Model is default fallback
  };

  const isMarketShareTypeChangeable = (rowIndex: number): boolean => {
    const rowData = editData[rowIndex];

    // If saved values exist, not changeable
    const hasSavedValues = rowData.data.some(item => item.isSavedMarketSharePresent);
    if (hasSavedValues) {
      return false;
    }

    // If only model values exist, not changeable
    const hasOldValues = rowData.data.some(item => item.isOldMarketSharePresent);
    if (!hasOldValues) {
      return false;
    }

    // If old values exist but no saved values, changeable between old and model
    return true;
  };

  const initializeMarketShareTypeForRow = (rowIndex: number) => {
    const gfcCotKey = getGFCCOTKey(rowIndex);

    // Only set if not already set
    if (!marketShareTypeSelection.has(gfcCotKey)) {
      const defaultType = getDefaultMarketShareType(rowIndex);
      setMarketShareTypeSelection(prev => {
        const newMap = new Map(prev);
        newMap.set(gfcCotKey, defaultType);
        return newMap;
      });
    }
  };

  const getForecastValue = (rowIndex: number, monthDateString: string) => {
    const gfcCotKey = getGFCCOTKey(rowIndex);
    const selectedType = marketShareTypeSelection.get(gfcCotKey) || 'model';
    return getMarketShareValueByType(rowIndex, monthDateString, selectedType);
  };

  const isNumeric = (value: string) => {
    return /^[0-9][0-9]?$|^100$/.test(value);
  };

  const _checkInqualityForCopyEditData = (
    rowData: msPlanBulkEditDataInterfaceDataInstanceInterface[],
    copyRowData: msPlanBulkEditDataInterfaceDataInstanceInterface[]
  ) => {
    for (let i = 0; i < rowData.length; i++) {
      const currentValue = rowData[i].MarketShare != null ? rowData[i].MarketShare.toString() : '';
      const copyValue =
        copyRowData[i].MarketShare != null ? copyRowData[i].MarketShare.toString() : '';
      if (currentValue !== copyValue) {
        return true;
      }
    }
    return false;
  };

  const handleSaveInputValue = (idx: number, period: string, adjustment: string) => {
    if (adjustment[0] === '0') {
      adjustment = adjustment.replace(/^0+/, '');
    }
    if (adjustment === '' || adjustment === '-') {
      adjustment = '0';
    }
    if (!isNumeric(adjustment)) {
      return; // Early return if adjustment is not numeric
    }

    const tempEditData = deepCopy(editData);
    const rowData = tempEditData[idx].data;
    const gfcCotKey = getGFCCOTKey(idx);
    const selectedType = marketShareTypeSelection.get(gfcCotKey) || 'model';

    for (let i = 0; i < rowData.length; i++) {
      if (rowData[i].PlanningMonth === period) {
        // Update the correct field based on selected type
        switch (selectedType) {
          case 'model':
            rowData[i].MarketShare = adjustment as unknown as number;
            break;
          case 'saved':
            rowData[i].Saved_MarketShare = Number(adjustment); // Convert back to decimal for saved
            rowData[i].isSavedMarketSharePresent = true;
            break;
          case 'old':
            rowData[i].Old_MarketShare = Number(adjustment); // Convert back to decimal for old
            rowData[i].isOldMarketSharePresent = true;
            break;
          default:
            rowData[i].MarketShare = adjustment as unknown as number;
        }

        // Check if row has been edited compared to original copy
        const originalRowData = editDataCopy[idx].data[i];
        let hasChanged = false;

        switch (selectedType) {
          case 'model':
            const currentMarketShare =
              rowData[i].MarketShare != null ? rowData[i].MarketShare.toString() : '';
            const originalMarketShare =
              originalRowData.MarketShare != null ? originalRowData.MarketShare.toString() : '';
            hasChanged = currentMarketShare !== originalMarketShare;
            break;
          case 'saved':
            hasChanged = rowData[i].Saved_MarketShare !== originalRowData.Saved_MarketShare;
            break;
          case 'old':
            hasChanged = rowData[i].Old_MarketShare !== originalRowData.Old_MarketShare;
            break;
        }

        if (!hasChanged) {
          GFCEditedSet.delete(`${rowData[i].GFC}-${rowData[i].COT}-${rowData[i].SellingCategory}`);
          const tempSet = new Set(gfcCotSellingCategoryError);
          tempSet.delete(`${rowData[i].GFC}-${rowData[i].COT}-${rowData[i].SellingCategory}`);
          setgfcCotSellingCategoryError(tempSet);
        } else {
          GFCEditedSet.add(`${rowData[i].GFC}-${rowData[i].COT}-${rowData[i].SellingCategory}`);
        }
      }
    }

    setEditData(tempEditData);
  };

  const isSaveButtonEnabled = (idx: number) => {
    if (idx < 0 || idx >= editData.length || !editData[idx]) {
      return false;
    }
    const rowData = editData[idx];
    const rowKey = `${rowData.GFC}-${rowData.COT}-${rowData.SellingCategory}`;
    return GFCEditedSet.has(rowKey);
  };

  const isSaveAllBtnEnabled = () => {
    // Check all rows to see if any have differences from targetMarketShare
    for (let idx = 0; idx < editData.length; idx++) {
      if (isSaveButtonEnabled(idx)) {
        return true;
      }
    }
    return false;
  };

  const getCurrentDate = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    return `${year}-${month}-01`;
  };

  const checkGfcCotSCisSaving = (rowIndex: number) => {
    if (rowIndex < 0 || rowIndex >= editData.length || !editData[rowIndex]) {
      return false;
    }
    const data = editData[rowIndex];
    return gfcCotSellingCategorySaveLoading.has(`${data.GFC}-${data.COT}-${data.SellingCategory}`);
  };

  const isNotesButtonEnabled = (rowIndex: number) => {
    if (rowIndex < 0 || rowIndex >= editData.length || !editData[rowIndex]) {
      return false;
    }
    const rowData = editData[rowIndex];
    const rowKey = `${rowData.GFC}-${rowData.COT}-${rowData.SellingCategory}`;
    return GFCEditedSet.has(rowKey);
  };

  const handleNoteButtonClick = (rowIndex: number) => {
    if (rowIndex < 0 || rowIndex >= editData.length || !editData[rowIndex]) {
      return;
    }
    getNotesData(editData[rowIndex].GFC, 'salesplan');
    setFocussedRowForNoteAdd(rowIndex);
  };

  const handleSingleSaveButtonClick = (index: number) => {
    setFocussedRowForSaving(index);
    setSingleSaveConfirmationModalVisibility(true);
  };

  const handleAllSaveButtonClick = () => {
    // setFocussedRowForSaving(index);
    setSaveAllConfirmationModalVisibility(true);
  };

  const markTheRowsSaved = (rowIndex: number[]) => {
    try {
      const tempEditData = deepCopy(editData);
      const tempSelectedType = new Map(marketShareTypeSelection);
      const tempEditDataCopy = deepCopy(editDataCopy);
      rowIndex.forEach(index => {
        const data = tempEditData[index].data;
        const gfcCotKey = getGFCCOTKey(index);
        const initialData = tempEditDataCopy[index].data;
        const selectedType = marketShareTypeSelection.get(gfcCotKey) || 'model';

        tempSelectedType.set(gfcCotKey, 'saved');

        data.forEach((element, index) => {
          let mktshare: number = 0;
          element.isSavedMarketSharePresent = true;
          initialData[index].isSavedMarketSharePresent = true;
          // Get the value based on selected market share type
          switch (selectedType) {
            case 'model':
              mktshare = Number(element.MarketShare);
              element.Saved_MarketShare = mktshare;
              initialData[index].Saved_MarketShare = mktshare;
              break;
            case 'saved':
              mktshare = element.isSavedMarketSharePresent ? Number(element.Saved_MarketShare) : 0;
              element.Saved_MarketShare = mktshare;
              initialData[index].Saved_MarketShare = mktshare;
              break;
            case 'old':
              mktshare = element.isOldMarketSharePresent ? Number(element.Old_MarketShare) : 0;
              element.Saved_MarketShare = mktshare;
              initialData[index].Saved_MarketShare = mktshare;
              break;
            default:
              mktshare = Number(element.MarketShare);
              element.Saved_MarketShare = mktshare;
              initialData[index].Saved_MarketShare = mktshare;
          }
        });
      });

      setEditData(tempEditData);
      setEditDataCopy(tempEditDataCopy);
      setMarketShareTypeSelection(tempSelectedType);
    } catch (_error) {
      console.log('Error while marking the rows saved', _error);
    }
  };

  const handleSingleSave = async () => {
    if (focussedRowForSaving === null) return;
    const data = editData[focussedRowForSaving];
    const originalData = editDataCopy[focussedRowForSaving];

    // Determine what type of values this row has and check for changes
    const hasSavedValues = data.data.some(item => item.isSavedMarketSharePresent);
    const hasOldValues = data.data.some(item => item.isOldMarketSharePresent);

    let _requiresNote = false;

    if (hasSavedValues) {
      // Check if saved values are different from initial saved values
      for (let i = 0; i < data.data.length; i++) {
        const currentSaved = data.data[i].isSavedMarketSharePresent
          ? Number(data.data[i].Saved_MarketShare)
          : 0;
        const originalSaved = originalData.data[i].isSavedMarketSharePresent
          ? Number(originalData.data[i].Saved_MarketShare)
          : 0;

        if (Math.abs(currentSaved - originalSaved) > 0.001) {
          _requiresNote = true;
          break;
        }
      }
    } else if (hasOldValues) {
      // Check if old values are different from initial old values
      for (let i = 0; i < data.data.length; i++) {
        const currentOld = data.data[i].isOldMarketSharePresent
          ? Number(data.data[i].Old_MarketShare)
          : 0;
        const originalOld = originalData.data[i].isOldMarketSharePresent
          ? Number(originalData.data[i].Old_MarketShare)
          : 0;

        if (Math.abs(currentOld - originalOld) > 0.001) {
          _requiresNote = true;
          break;
        }
      }
    } else {
      // Check if market share values are different from initial market share values
      for (let i = 0; i < data.data.length; i++) {
        const currentMarketShare = Number(data.data[i].MarketShare);
        const originalMarketShare = Number(originalData.data[i].MarketShare);

        if (Math.abs(currentMarketShare - originalMarketShare) > 0.001) {
          _requiresNote = true;
          break;
        }
      }
    }

    // Proceed with save (either no changes detected or note provided)
    // Remove from error set
    const tempSet = new Set(gfcCotSellingCategoryError);
    tempSet.delete(`${data.GFC}-${data.COT}-${data.SellingCategory}`);
    setgfcCotSellingCategoryError(tempSet);

    // save the ndc data
    const payload: {
      MarketShareID: number;
      TMSPlanningMonth: string;
      Adjustment: number;
      MarketSharePlan: number;
      Note: string;
      PlanningPeriod: string;
    }[] = [];
    const planning_period = getCurrentDate();
    const gfcCotKey = getGFCCOTKey(focussedRowForSaving);
    const selectedType = marketShareTypeSelection.get(gfcCotKey) || 'model';

    data.data.forEach(element => {
      if (element.MarketShareID === null) return;

      let mktshare: number = 0;

      // Get the value based on selected market share type
      switch (selectedType) {
        case 'model':
          mktshare = Number(element.MarketShare);
          break;
        case 'saved':
          mktshare = element.isSavedMarketSharePresent ? Number(element.Saved_MarketShare) : 0;
          break;
        case 'old':
          mktshare = element.isOldMarketSharePresent ? Number(element.Old_MarketShare) : 0;
          break;
        default:
          mktshare = Number(element.MarketShare);
      }

      const marketShare = Number((Number(mktshare) / 100).toFixed(2));
      const targetMarketShare = Number(element.TargetMarketShare);
      const adjustment = Number((marketShare - targetMarketShare).toFixed(2));

      payload.push({
        MarketShareID: element.MarketShareID,
        TMSPlanningMonth: element.PlanningMonth,
        Adjustment: adjustment,
        MarketSharePlan: marketShare,
        Note: 'note',
        PlanningPeriod: planning_period,
      });
    });

    try {
      //  Add the data in the loading set
      const focussedRowData = editData[focussedRowForSaving];
      const loadingSet = new Set(gfcCotSellingCategorySaveLoading);
      loadingSet.add(
        `${focussedRowData.GFC}-${focussedRowData.COT}-${focussedRowData.SellingCategory}`
      );
      setGfcCotSellingCategorySaveLoading(loadingSet);

      const data = await tmsScreenServices.updateTMSData(payload);
      if (data) {
        const tempCopyData = deepCopy(editDataCopy);
        tempCopyData[focussedRowForSaving] = editData[focussedRowForSaving];
        setEditDataCopy(tempCopyData);

        const tempSet = new Set(GFCEditedSet);
        tempSet.delete(
          `${focussedRowData.GFC}-${focussedRowData.COT}-${focussedRowData.SellingCategory}`
        );
        setGFCEditedSet(tempSet);

        // Remove all notes for this row from the set after successful save
        const tempNoteSet = new Set(GFCNotesSet);
        for (const noteKey of Array.from(tempNoteSet)) {
          if (
            noteKey.startsWith(
              `${focussedRowData.GFC}-${focussedRowData.COT}-${focussedRowData.SellingCategory}-`
            )
          ) {
            tempNoteSet.delete(noteKey);
          }
        }
        setGFCNotesSet(tempNoteSet);

        setSingleSaveConfirmationModalVisibility(false);
        setFocussedRowForNoteAdd(null);
      }
      toast.success('Updated Successfully!');
      markTheRowsSaved([focussedRowForSaving]);
    } catch (_error) {
      console.log(`Error while saving the Sales plan`, _error);
      toast.error('Something went wrong while saving Sales Plan');
    } finally {
      const focussedRowData = editData[focussedRowForSaving];
      const loadingSet = new Set(gfcCotSellingCategorySaveLoading);
      loadingSet.delete(
        `${focussedRowData.GFC}-${focussedRowData.COT}-${focussedRowData.SellingCategory}`
      );
      setGfcCotSellingCategorySaveLoading(loadingSet);
    }
  };

  const handleSaveAllEdited = async () => {
    setIsSaveloading(true);

    // First, check each row for note requirements
    const rowsToSave: number[] = [];

    for (let i = 0; i < editData.length; i++) {
      const data = editData[i];
      const originalData = editDataCopy[i];

      // Determine what type of values this row has and check for changes
      const hasSavedValues = data.data.some(item => item.isSavedMarketSharePresent);
      const hasOldValues = data.data.some(item => item.isOldMarketSharePresent);

      let requiresNote = false;

      if (hasSavedValues) {
        // Check if saved values are different from initial saved values
        for (let j = 0; j < data.data.length; j++) {
          const currentSaved = data.data[j].isSavedMarketSharePresent
            ? Number(data.data[j].Saved_MarketShare)
            : 0;
          const originalSaved = originalData.data[j].isSavedMarketSharePresent
            ? Number(originalData.data[j].Saved_MarketShare)
            : 0;

          if (Math.abs(currentSaved - originalSaved) > 0.001) {
            requiresNote = true;
            break;
          }
        }
      } else if (hasOldValues) {
        // Check if old values are different from initial old values
        for (let j = 0; j < data.data.length; j++) {
          const currentOld = data.data[j].isOldMarketSharePresent
            ? Number(data.data[j].Old_MarketShare)
            : 0;
          const originalOld = originalData.data[j].isOldMarketSharePresent
            ? Number(originalData.data[j].Old_MarketShare)
            : 0;

          if (Math.abs(currentOld - originalOld) > 0.001) {
            requiresNote = true;
            break;
          }
        }
      } else {
        // Check if market share values are different from initial market share values
        for (let j = 0; j < data.data.length; j++) {
          const currentMarketShare = Number(data.data[j].MarketShare);
          const originalMarketShare = Number(originalData.data[j].MarketShare);

          if (Math.abs(currentMarketShare - originalMarketShare) > 0.001) {
            requiresNote = true;
            break;
          }
        }
      }

      // Check if this row should be included in save
      if (requiresNote || !hasSavedValues) {
        // Include rows that require notes OR rows without saved values
        rowsToSave.push(i);
      }
    }

    // Clear errors since all requirements are met
    setgfcCotSellingCategoryError(new Set());

    // Proceed with save for qualifying rows
    const payload: {
      MarketShareID: number;
      TMSPlanningMonth: string;
      Adjustment: number;
      MarketSharePlan: number;
      Note: string;
      PlanningPeriod: string;
    }[] = [];
    const planning_period = getCurrentDate();

    for (const rowIndex of rowsToSave) {
      const data = editData[rowIndex];
      const gfcCotKey = getGFCCOTKey(rowIndex);
      const selectedType = marketShareTypeSelection.get(gfcCotKey) || 'model';

      data.data.forEach(element => {
        if (element.MarketShareID === null) return;
        let mktshare: number = 0;

        // Get the value based on selected market share type
        switch (selectedType) {
          case 'model':
            mktshare = Number(element.MarketShare);
            break;
          case 'saved':
            mktshare = element.isSavedMarketSharePresent ? Number(element.Saved_MarketShare) : 0;
            break;
          case 'old':
            mktshare = element.isOldMarketSharePresent ? Number(element.Old_MarketShare) : 0;
            break;
          default:
            mktshare = Number(element.MarketShare);
        }

        const marketShare = Number((Number(mktshare) / 100).toFixed(2));
        const targetMarketShare = Number(element.TargetMarketShare);
        const adjustment = Number((marketShare - targetMarketShare).toFixed(2));

        payload.push({
          MarketShareID: element.MarketShareID,
          TMSPlanningMonth: element.PlanningMonth,
          Adjustment: adjustment,
          MarketSharePlan: marketShare,
          Note: 'note',
          PlanningPeriod: planning_period,
        });
      });
    }

    try {
      const data = await tmsScreenServices.updateTMSData(payload);
      if (data) {
        setEditDataCopy(deepCopy(editData));
        setGFCEditedSet(new Set());
        setgfcCotSellingCategoryError(new Set());
      }
      toast.success('Updated Successfully!');
      markTheRowsSaved(rowsToSave);
    } catch (_error) {
      console.log(`Error while saving the Sales plan`, _error);
      toast.error('Something went wrong while saving Sales Plan');
    } finally {
      setGFCNotesSet(new Set());
      setSaveAllConfirmationModalVisibility(false);
      setIsSaveloading(false);
    }
  };

  const getNextPageData = async () => {
    const capturedRequestId = requestIdRef.current;
    try {
      setNextPageLoading(true);
      const data = await tmsScreenServices.getFilteredDataForBulkEditMSPlan({
        page_no: pageNumber,
        filters: filterData,
      });
      if (capturedRequestId !== requestIdRef.current) return;
      if (data.length === 0) {
        setHasMore(false);
        return;
      }
      setPageNumber(pageNumber + 1);
      if (data) {
        const currentDataLength = editData.length;
        setEditData([...editData, ...data]);
        setEditDataCopy(deepCopy([...editDataCopy, ...data]));

        // Initialize market share types for new rows
        data.forEach((_, index) => {
          initializeMarketShareTypeForRow(currentDataLength + index);
        });
      }
    } catch (_error) {
      console.log('Error while getting the data');
    } finally {
      setNextPageLoading(false);
      setInitialLoading(false);
    }
  };

  const loadMore = async () => {
    if (nextPageLoading || !hasMore) {
      return;
    }
    setNextPageLoading(true);
    getNextPageData();
  };

  const handleApplyFilter = (newFilterData: msPlanBulkEditFilterInterface) => {
    requestIdRef.current += 1;
    setEditData([]);
    setEditDataCopy([]);
    setGfcCotSellingCategorySaveLoading(new Set());
    setGFCEditedSet(new Set());
    setGFCNotesSet(new Set());
    setSavingGFCCotSC(new Set());
    setMarketShareTypeSelection(new Map());
    setExpandedRows(new Set());
    setFilterData(newFilterData);
    setHasMore(true);
    setPageNumber(0);
    fetchDataWithSort(newFilterData);
  };

  const _getInitialData = async () => {
    try {
      setIsDataLoading(true);
      const data = await tmsScreenServices.getFilteredDataForBulkEditMSPlan({
        page_no: 0, // Start from 0
        filters: filterData,
      });
      setPageNumber(1); // Set to 1 since we just loaded page 0
      if (data.length === 0) {
        setHasMore(false);
        return;
      }
      if (data) {
        setEditData(data);
        setEditDataCopy(deepCopy(data));

        // Initialize market share types for all rows
        data.forEach((_, index) => {
          initializeMarketShareTypeForRow(index);
        });
      }
    } catch (_error) {
      console.log('Error while getting the data');
    } finally {
      setIsDataLoading(false);
    }
  };

  const toggleVisibilityForNotes = () => {
    setFocussedRowForNoteAdd(null);
    setNotesData([]);
  };

  const checkIfRowHasError = (rowIndex: number) => {
    const data = editData[rowIndex];
    return gfcCotSellingCategoryError.has(`${data.GFC}-${data.COT}-${data.SellingCategory}`);
  };

  const handleGoToSalesPlan = () => {
    navigate('/fdp/target_market/bulk_edit/salesplan');
    toggleVisibilityForRouteConfirmationPopUp();
  };

  const handleSalesPlanButtonClick = () => {
    if (GFCEditedSet.size == 0) {
      handleGoToSalesPlan();
    } else {
      setIsRouteChangeConfirmationPopUpVisible(true);
    }
  };

  const toggleVisibilityForRouteConfirmationPopUp = () => {
    setIsRouteChangeConfirmationPopUpVisible(false);
  };

  const handleGoToTMSViewScreen = () => {
    navigate('/fdp/target_market');
    setisCancelConfirmationPopVisible(false);
  };

  const handleCancelButtonClick = () => {
    if (GFCEditedSet.size == 0) {
      handleGoToTMSViewScreen();
    } else {
      setisCancelConfirmationPopVisible(true);
    }
  };

  const toggleCancelConfirmationPopUpVisibility = () => {
    setisCancelConfirmationPopVisible(!isCancelConfirmationPopVisible);
  };

  const handleCopy = (idx: number, month: string) => {
    const tempData = deepCopy(editData);
    const gfcCotKey = getGFCCOTKey(idx);
    const selectedType = marketShareTypeSelection.get(gfcCotKey) || 'model';

    // Get the value from the selected type
    let sourceValue: number = 0;
    const sourceData = editData[idx].data.filter(item => item.PlanningMonth === month)[0];

    switch (selectedType) {
      case 'model':
        sourceValue = Number(sourceData.MarketShare);
        break;
      case 'saved':
        sourceValue = sourceData.isSavedMarketSharePresent
          ? Number(sourceData.Saved_MarketShare)
          : 0;
        break;
      case 'old':
        sourceValue = sourceData.isOldMarketSharePresent ? Number(sourceData.Old_MarketShare) : 0;
        break;
      default:
        sourceValue = Number(sourceData.MarketShare);
    }

    const forecastDate = new Date(month);

    // copying all the data in cells to the right
    for (const item of tempData[idx].data) {
      if (new Date(item.PlanningMonth) > forecastDate) {
        switch (selectedType) {
          case 'model':
            item.MarketShare = Number(sourceValue) || 0;
            break;
          case 'saved':
            item.Saved_MarketShare = Number(sourceValue) || 0;
            item.isSavedMarketSharePresent = true;
            break;
          case 'old':
            item.Old_MarketShare = Number(sourceValue) || 0;
            item.isOldMarketSharePresent = true;
            break;
          default:
            item.MarketShare = Number(sourceValue) || 0;
        }
      }
    }

    // Check if any values have changed compared to original
    let copyTestFlag = true;
    for (let i = 0; i < tempData[idx].data.length; i++) {
      let hasChanged = false;
      const originalItem = editDataCopy[idx].data[i];
      const currentItem = tempData[idx].data[i];

      switch (selectedType) {
        case 'model':
          hasChanged = currentItem.MarketShare !== originalItem.MarketShare;
          break;
        case 'saved':
          hasChanged = currentItem.Saved_MarketShare !== originalItem.Saved_MarketShare;
          break;
        case 'old':
          hasChanged = currentItem.Old_MarketShare !== originalItem.Old_MarketShare;
          break;
      }

      if (hasChanged) {
        copyTestFlag = false;
        break;
      }
    }

    if (copyTestFlag) {
      GFCEditedSet.delete(
        `${tempData[idx].GFC}-${tempData[idx].COT}-${tempData[idx].SellingCategory}`
      );
    } else {
      GFCEditedSet.add(
        `${tempData[idx].GFC}-${tempData[idx].COT}-${tempData[idx].SellingCategory}`
      );
    }

    setEditData(tempData);
  };

  const toggleRowExpansion = (rowIndex: number) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowIndex)) {
        newSet.delete(rowIndex);
      } else {
        newSet.add(rowIndex);
      }
      return newSet;
    });
  };

  const handleMarketShareTypeChange = (rowIndex: number, type: MarketShareType) => {
    const gfcCotKey = getGFCCOTKey(rowIndex);
    const defaultType = getDefaultMarketShareType(rowIndex);
    const rowData = editData[rowIndex];
    const rowKey = `${rowData.GFC}-${rowData.COT}-${rowData.SellingCategory}`;

    if (type !== defaultType) {
      GFCEditedSet.add(rowKey);
    } else {
      // Reverting to default type — only clear edited flag if values also match the copy
      const currentData = rowData.data;
      const copyData = editDataCopy[rowIndex].data;
      let hasValueEdits = false;
      for (let i = 0; i < currentData.length; i++) {
        switch (type) {
          case 'model':
            hasValueEdits =
              String(currentData[i].MarketShare) !== String(copyData[i].MarketShare);
            break;
          case 'old':
            hasValueEdits = currentData[i].Old_MarketShare !== copyData[i].Old_MarketShare;
            break;
          case 'saved':
            hasValueEdits =
              currentData[i].Saved_MarketShare !== copyData[i].Saved_MarketShare;
            break;
        }
        if (hasValueEdits) break;
      }
      if (!hasValueEdits) {
        GFCEditedSet.delete(rowKey);
      }
    }

    setMarketShareTypeSelection(prev => {
      const newMap = new Map(prev);
      newMap.set(gfcCotKey, type);
      return newMap;
    });
  };

  const getMarketShareValueByType = (
    rowIndex: number,
    monthDateString: string,
    type: MarketShareType
  ): string => {
    const data = editData[rowIndex].data;
    for (let i = 0; i < data.length; i++) {
      if (data[i].PlanningMonth === monthDateString) {
        switch (type) {
          case 'model':
            return data[i].MarketShare != null ? data[i].MarketShare.toString() : '-';
          case 'old':
            return data[i].isOldMarketSharePresent && data[i].Old_MarketShare != null
              ? Math.round(Number(data[i].Old_MarketShare)).toString()
              : '-';
          case 'saved':
            return data[i].isSavedMarketSharePresent && data[i].Saved_MarketShare != null
              ? Math.round(Number(data[i].Saved_MarketShare)).toString()
              : '-';
          default:
            return data[i].MarketShare != null ? data[i].MarketShare.toString() : '-';
        }
      }
    }
    return '-';
  };

  const isRowExpanded = (rowIndex: number): boolean => {
    return expandedRows.has(rowIndex);
  };

  const getSelectedMarketShareType = (rowIndex: number): MarketShareType => {
    const gfcCotKey = getGFCCOTKey(rowIndex);

    // Initialize if not set
    if (!marketShareTypeSelection.has(gfcCotKey)) {
      const defaultType = getDefaultMarketShareType(rowIndex);
      setMarketShareTypeSelection(prev => {
        const newMap = new Map(prev);
        newMap.set(gfcCotKey, defaultType);
        return newMap;
      });
      return defaultType;
    }

    return marketShareTypeSelection.get(gfcCotKey) || getDefaultMarketShareType(rowIndex);
  };

  const handleConceptSort = (sortType: 'asc' | 'desc' | '') => {
    requestIdRef.current += 1;
    // Clear all existing data
    setEditData([]);
    setEditDataCopy([]);
    setGfcCotSellingCategorySaveLoading(new Set());
    setGFCEditedSet(new Set());
    setGFCNotesSet(new Set());
    setSavingGFCCotSC(new Set());
    setMarketShareTypeSelection(new Map());
    setExpandedRows(new Set());
    setgfcCotSellingCategoryError(new Set());

    // Update filter data with new sort
    const newFilterData = {
      ...filterData,
      ConceptSort: sortType,
    };
    setFilterData(newFilterData);
    setHasMore(true);
    setPageNumber(0); // Start from 0

    // Fetch data with new sort immediately
    fetchDataWithSort(newFilterData);
  };

  const fetchDataWithSort = async (filterDataWithSort: msPlanBulkEditFilterInterface) => {
    const capturedRequestId = requestIdRef.current;
    try {
      setIsDataLoading(true);
      setInitialLoading(true);
      const data = await tmsScreenServices.getFilteredDataForBulkEditMSPlan({
        page_no: 0, // Start from 0
        filters: filterDataWithSort,
      });
      if (capturedRequestId !== requestIdRef.current) return;
      setPageNumber(1); // Set to 1 since we just loaded page 0
      if (data.length === 0) {
        setHasMore(false);
        return;
      }
      if (data) {
        setEditData(data);
        setEditDataCopy(deepCopy(data));

        // Initialize market share types for all rows
        data.forEach((_, index) => {
          initializeMarketShareTypeForRow(index);
        });
      }
    } catch (_error) {
      console.log('Error while getting the data');
    } finally {
      if (capturedRequestId === requestIdRef.current) {
        setIsDataLoading(false);
        setInitialLoading(false);
      }
    }
  };

  const getRowStatus = (rowIndex: number): 'model' | 'manual' => {
    const gfcCotKey = getGFCCOTKey(rowIndex);
    const selectedType = marketShareTypeSelection.get(gfcCotKey) || 'model';
    const rowData = editData[rowIndex];

    // Check if any month's selected value differs from targetMarketShare
    for (let i = 0; i < rowData.data.length; i++) {
      const monthData = rowData.data[i];
      let selectedValue: number = 0;

      // Get the current value based on selected type
      switch (selectedType) {
        case 'model':
          selectedValue = Number(monthData.MarketShare);
          break;
        case 'saved':
          selectedValue = monthData.isSavedMarketSharePresent
            ? Number(monthData.Saved_MarketShare)
            : 0;
          break;
        case 'old':
          selectedValue = monthData.isOldMarketSharePresent ? Number(monthData.Old_MarketShare) : 0;
          break;
      }

      const targetValue = Number(monthData.TargetMarketShare) * 100;

      // If any month differs from target, it's manual
      if (Math.abs(selectedValue - targetValue) > 0.001) {
        return 'manual';
      }
    }

    // All months match target, so it's model
    return 'model';
  };

  const shouldShowToggle = (rowIndex: number): boolean => {
    const rowData = editData[rowIndex];

    // Check if row has old values but no saved values
    const hasOldValues = rowData.data.some(item => item.isOldMarketSharePresent);
    const hasSavedValues = rowData.data.some(item => item.isSavedMarketSharePresent);

    // Show toggle only if has old values and no saved values (model is always available)
    return hasOldValues && !hasSavedValues;
  };

  // useEffect(() => {
  //   getInitialData();
  // }, []);

  // useEffect(() => {
  //   getInitialData();
  // }, [filterData]);

  return {
    editData,
    setEditData,
    setEditDataCopy,
    handleApplyFilter,
    filterData,
    pageNumber,
    setPageNumber,
    isDataLoading,
    setIsDataLoading,
    getForecastValue,
    GFCNotesSet,
    setGFCNotesSet,
    gfcCotSellingCategoryError,
    setgfcCotSellingCategoryError,
    handleSingleSave,
    handleSaveAllEdited,
    gfcCotSellingCategorySaveLoading,
    isSaveAllLoading,
    isNotesLoading,
    notesData,
    setNotesData,
    isNoteEdited,
    setIsNoteEdited,
    isNoteDeleted,
    setIsNoteDeleted,
    isNoteSaving,
    getNotesData,
    handleAddNote,
    hasMore,
    nextPageLoading,
    loadMore,
    toggleVisibilityForNotes,
    handleSaveInputValue,
    isSaveAllBtnEnabled,
    checkGfcCotSCisSaving,
    isNotesButtonEnabled,
    handleNoteButtonClick,
    checkIfRowHasError,
    isSaveButtonEnabled,
    focussedRowForNoteAdd,
    singleSaveConfirmationModalVisibility,
    saveAllConfirmationModalVisibility,
    setSingleSaveConfirmationModalVisibility,
    setSaveAllConfirmationModalVisibility,
    focussedRowForSaving,
    handleSingleSaveButtonClick,
    handleAllSaveButtonClick,
    isRouteChangeConfirmationPopUpVisible,
    handleSalesPlanButtonClick,
    handleGoToSalesPlan,
    toggleVisibilityForRouteConfirmationPopUp,
    GFCEditedSet,
    isCancelConfirmationPopVisible,
    handleCancelButtonClick,
    toggleCancelConfirmationPopUpVisibility,
    handleGoToTMSViewScreen,
    handleCopy,
    handleEditNote,
    handleDeleteNote,
    deletingNote,
    isNoteDeleting,
    isDeleteClicked,
    setIsDeleteClicked,
    setDeletingNote,
    initialLoading,
    // New exports for accordion functionality
    expandedRows,
    toggleRowExpansion,
    handleMarketShareTypeChange,
    getMarketShareValueByType,
    isRowExpanded,
    getSelectedMarketShareType,
    // New exports for business rules
    getAvailableMarketShareTypes,
    getDefaultMarketShareType,
    isMarketShareTypeChangeable,
    // New export for concept sorting
    handleConceptSort,
    // New export for row status
    getRowStatus,
    // New export for toggle display
    shouldShowToggle,
  };
};
